/**
 * Lightning background effect — detailed thunderstrike
 * Multi-layered bolts with sub-branches, jitter, ambient crackle, and impact glow.
 */
(function () {
  'use strict';

  // Honour the user's reduced-motion preference: when set, we don't render
  // any bolts at all (the effect is decorative and irrelevant for a11y).
  var REDUCED_MOTION = typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function LightningCanvas(container) {
    this.container = container;
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'pc3d-lightning-canvas';
    this.ctx = this.canvas.getContext('2d');
    container.appendChild(this.canvas);

    this.bolts = [];
    this.flashOpacity = 0;
    this.ambientCrackles = [];
    this.lastBoltTime = 0;
    this.nextBoltDelay = this._randomDelay();
    this.running = true;

    // Pause flags driven by intersection + tab visibility. We only render
    // when the section is on-screen AND the tab is visible AND the user
    // hasn't requested reduced motion.
    this.inView = true;
    this.tabVisible = (typeof document.visibilityState === 'undefined')
      || document.visibilityState === 'visible';
    this.rafId = null;

    this._resize();
    this._onResize = this._resize.bind(this);
    window.addEventListener('resize', this._onResize);

    // Pause the loop when the section scrolls out of view — saves CPU/GPU on
    // long pages where the carousel sits below the fold most of the time.
    this._observer = null;
    if ('IntersectionObserver' in window) {
      var self = this;
      this._observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          self.inView = entry.isIntersecting;
          self._maybeRunOrPause();
        });
      }, { threshold: 0.01 });
      this._observer.observe(container);
    }

    // Pause when the browser tab is hidden — no point burning frames the user
    // can't see, and prevents bolt timestamps from drifting in the background.
    this._onVisibilityChange = function () {
      this.tabVisible = document.visibilityState === 'visible';
      this._maybeRunOrPause();
    }.bind(this);
    document.addEventListener('visibilitychange', this._onVisibilityChange);

    if (REDUCED_MOTION) {
      // Still set up the canvas (so destroy() / resize work) but never spawn bolts.
      this.running = false;
    } else {
      this._loop();
    }
  }

  /**
   * Decide whether the animation should be running based on intersection +
   * tab visibility. Restarts the rAF loop or stops it accordingly.
   */
  LightningCanvas.prototype._maybeRunOrPause = function () {
    if (REDUCED_MOTION) return;
    var shouldRun = this.inView && this.tabVisible;
    if (shouldRun && !this.running) {
      this.running = true;
      // Reset bolt timer so we don't immediately spawn a flurry from the
      // accumulated delay while paused.
      this.lastBoltTime = performance.now();
      this._loop();
    } else if (!shouldRun && this.running) {
      this.running = false;
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }
  };

  LightningCanvas.prototype._randomDelay = function () {
    return 3000 + Math.random() * 4000;
  };

  LightningCanvas.prototype._resize = function () {
    var rect = this.canvas.parentElement.getBoundingClientRect();
    this.w = rect.width;
    this.h = rect.height;
    this.canvas.width = this.w * window.devicePixelRatio;
    this.canvas.height = this.h * window.devicePixelRatio;
    this.canvas.style.width = this.w + 'px';
    this.canvas.style.height = this.h + 'px';
    this.ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
  };

  /**
   * Generate a jagged path with fine detail segments
   */
  LightningCanvas.prototype._generatePath = function (startX, startY, targetY, jag, stepMin, stepMax) {
    var segments = [];
    var x = startX;
    var y = startY;
    while (y < targetY) {
      var stepY = stepMin + Math.random() * (stepMax - stepMin);
      var stepX = (Math.random() - 0.5) * jag;
      x += stepX;
      y += stepY;
      segments.push({ x: x, y: y });
    }
    return segments;
  };

  LightningCanvas.prototype._createBolt = function () {
    var startX = this.w * (0.12 + Math.random() * 0.76);
    var startY = 0;
    var targetY = this.h * (0.65 + Math.random() * 0.35);

    // Main bolt — fine detail with small steps
    var mainSegs = this._generatePath(startX, startY, targetY, 25, 3, 8);

    // Primary branches (large)
    var branches = [];
    var branchCount = 3 + Math.floor(Math.random() * 4);
    for (var b = 0; b < branchCount; b++) {
      var idx = Math.floor(Math.random() * mainSegs.length * 0.75);
      if (!mainSegs[idx]) continue;
      var origin = mainSegs[idx];
      var dir = Math.random() > 0.5 ? 1 : -1;
      var branchTargetY = origin.y + 40 + Math.random() * 120;
      var branchSegs = [];
      var bx = origin.x;
      var by = origin.y;
      var branchLen = 3 + Math.floor(Math.random() * 5);
      for (var s = 0; s < branchLen; s++) {
        bx += dir * (3 + Math.random() * 12);
        by += 3 + Math.random() * 8;
        branchSegs.push({ x: bx, y: by });
      }

      // Sub-branches (small forks off branches)
      var subBranches = [];
      if (branchSegs.length > 3 && Math.random() > 0.4) {
        var subIdx = Math.floor(Math.random() * branchSegs.length * 0.6);
        var subOrigin = branchSegs[subIdx];
        if (subOrigin) {
          var subSegs = [];
          var sx = subOrigin.x;
          var sy = subOrigin.y;
          var subDir = Math.random() > 0.5 ? 1 : -1;
          var subLen = 2 + Math.floor(Math.random() * 3);
          for (var ss = 0; ss < subLen; ss++) {
            sx += subDir * (2 + Math.random() * 8);
            sy += 3 + Math.random() * 6;
            subSegs.push({ x: sx, y: sy });
          }
          subBranches.push({ start: subOrigin, segs: subSegs });
        }
      }

      branches.push({ start: origin, segs: branchSegs, subBranches: subBranches });
    }

    // Impact point glow
    var impactPoint = mainSegs.length > 0 ? mainSegs[mainSegs.length - 1] : { x: startX, y: targetY };

    var now = performance.now();

    return {
      startX: startX,
      startY: startY,
      segments: mainSegs,
      branches: branches,
      impactPoint: impactPoint,
      width: 1 + Math.random() * 0.8,
      spawnTime: now,
      flashDuration: 120,
      holdDuration: 2000,
      fadeDuration: 1200,
      // Jitter — slight random displacement per frame during hold
      jitterAmount: 1.5
    };
  };

  /**
   * Create small ambient crackle bolts (background detail)
   */
  LightningCanvas.prototype._spawnAmbientCrackle = function () {
    var startX = this.w * (0.05 + Math.random() * 0.9);
    var startY = Math.random() * this.h * 0.5;
    var segs = this._generatePath(startX, startY, startY + 15 + Math.random() * 40, 15, 2, 6);
    return {
      segments: segs,
      startX: startX,
      startY: startY,
      life: 1.0,
      decay: 0.06 + Math.random() * 0.06,
      width: 0.3 + Math.random() * 0.5
    };
  };

  LightningCanvas.prototype._getBoltAlpha = function (bolt, now) {
    var elapsed = now - bolt.spawnTime;
    if (elapsed < bolt.flashDuration) {
      return 1.0;
    } else if (elapsed < bolt.flashDuration + bolt.holdDuration) {
      return 0.75 + Math.random() * 0.25;
    } else if (elapsed < bolt.flashDuration + bolt.holdDuration + bolt.fadeDuration) {
      var fadeProgress = (elapsed - bolt.flashDuration - bolt.holdDuration) / bolt.fadeDuration;
      return (1 - fadeProgress) * 0.75;
    }
    return 0;
  };

  LightningCanvas.prototype._isBoltDead = function (bolt, now) {
    return (now - bolt.spawnTime) > (bolt.flashDuration + bolt.holdDuration + bolt.fadeDuration);
  };

  LightningCanvas.prototype._tracePathJittered = function (ctx, startX, startY, segments, jitter) {
    ctx.beginPath();
    ctx.moveTo(startX + (Math.random() - 0.5) * jitter, startY);
    for (var i = 0; i < segments.length; i++) {
      ctx.lineTo(
        segments[i].x + (Math.random() - 0.5) * jitter,
        segments[i].y + (Math.random() - 0.5) * jitter * 0.5
      );
    }
  };

  LightningCanvas.prototype._tracePath = function (ctx, startX, startY, segments) {
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    for (var i = 0; i < segments.length; i++) {
      ctx.lineTo(segments[i].x, segments[i].y);
    }
  };

  LightningCanvas.prototype._drawBolt = function (bolt, alpha) {
    var ctx = this.ctx;
    var elapsed = performance.now() - bolt.spawnTime;
    var inHold = elapsed > bolt.flashDuration && elapsed < bolt.flashDuration + bolt.holdDuration;
    var jitter = inHold ? bolt.jitterAmount : 0;

    // Layer 1: Ultra-wide ambient glow
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 190, 40, ' + (alpha * 0.15) + ')';
    ctx.lineWidth = bolt.width * 12;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = 'rgba(255, 170, 20, ' + (alpha * 0.2) + ')';
    ctx.shadowBlur = 30;
    this._tracePathJittered(ctx, bolt.startX, bolt.startY, bolt.segments, jitter);
    ctx.stroke();
    ctx.restore();

    // Layer 2: Wide glow
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 200, 50, ' + (alpha * 0.35) + ')';
    ctx.lineWidth = bolt.width * 5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = 'rgba(255, 180, 30, ' + (alpha * 0.4) + ')';
    ctx.shadowBlur = 18;
    this._tracePathJittered(ctx, bolt.startX, bolt.startY, bolt.segments, jitter);
    ctx.stroke();

    // Branch glow
    for (var b = 0; b < bolt.branches.length; b++) {
      var branch = bolt.branches[b];
      ctx.lineWidth = bolt.width * 4;
      this._tracePathJittered(ctx, branch.start.x, branch.start.y, branch.segs, jitter);
      ctx.stroke();
      // Sub-branch glow
      for (var sb = 0; sb < branch.subBranches.length; sb++) {
        ctx.lineWidth = bolt.width * 2;
        this._tracePathJittered(ctx, branch.subBranches[sb].start.x, branch.subBranches[sb].start.y, branch.subBranches[sb].segs, jitter);
        ctx.stroke();
      }
    }
    ctx.restore();

    // Layer 3: Bright core
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 240, 170, ' + alpha + ')';
    ctx.lineWidth = bolt.width * 1.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = 'rgba(255, 230, 100, ' + (alpha * 0.8) + ')';
    ctx.shadowBlur = 12;
    this._tracePathJittered(ctx, bolt.startX, bolt.startY, bolt.segments, jitter * 0.5);
    ctx.stroke();

    for (var c = 0; c < bolt.branches.length; c++) {
      var br = bolt.branches[c];
      ctx.lineWidth = bolt.width * 0.6;
      this._tracePathJittered(ctx, br.start.x, br.start.y, br.segs, jitter * 0.5);
      ctx.stroke();
      for (var sc = 0; sc < br.subBranches.length; sc++) {
        ctx.lineWidth = bolt.width * 0.3;
        this._tracePathJittered(ctx, br.subBranches[sc].start.x, br.subBranches[sc].start.y, br.subBranches[sc].segs, jitter * 0.5);
        ctx.stroke();
      }
    }
    ctx.restore();

    // Layer 4: White-hot inner core (flash only)
    if (alpha > 0.9) {
      ctx.save();
      ctx.strokeStyle = 'rgba(255, 255, 255, ' + (alpha * 0.7) + ')';
      ctx.lineWidth = bolt.width * 0.35;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      this._tracePath(ctx, bolt.startX, bolt.startY, bolt.segments);
      ctx.stroke();
      ctx.restore();
    }

    // Impact glow — radial light at strike point
    if (alpha > 0.3) {
      ctx.save();
      var ip = bolt.impactPoint;
      var gradient = ctx.createRadialGradient(ip.x, ip.y, 0, ip.x, ip.y, 40 + alpha * 20);
      gradient.addColorStop(0, 'rgba(255, 220, 80, ' + (alpha * 0.3) + ')');
      gradient.addColorStop(0.4, 'rgba(255, 180, 30, ' + (alpha * 0.1) + ')');
      gradient.addColorStop(1, 'rgba(255, 150, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(ip.x, ip.y, 60, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  };

  LightningCanvas.prototype._drawCrackle = function (crackle) {
    var ctx = this.ctx;
    var alpha = crackle.life;

    ctx.save();
    ctx.strokeStyle = 'rgba(255, 210, 80, ' + (alpha * 0.3) + ')';
    ctx.lineWidth = crackle.width * 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = 'rgba(255, 190, 40, ' + (alpha * 0.2) + ')';
    ctx.shadowBlur = 15;
    this._tracePath(ctx, crackle.startX, crackle.startY, crackle.segments);
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = 'rgba(255, 240, 160, ' + (alpha * 0.6) + ')';
    ctx.lineWidth = crackle.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    this._tracePath(ctx, crackle.startX, crackle.startY, crackle.segments);
    ctx.stroke();
    ctx.restore();
  };

  LightningCanvas.prototype._loop = function () {
    if (!this.running) return;
    var self = this;
    this.rafId = requestAnimationFrame(function () { self._loop(); });

    var ctx = this.ctx;
    var now = performance.now();
    ctx.clearRect(0, 0, this.w, this.h);

    // Flash overlay
    if (this.flashOpacity > 0) {
      ctx.save();
      ctx.fillStyle = 'rgba(255, 220, 80, ' + (this.flashOpacity * 0.1) + ')';
      ctx.fillRect(0, 0, this.w, this.h);
      ctx.restore();
      this.flashOpacity -= 0.04;
    }

    // Spawn main bolt
    if (now - this.lastBoltTime > this.nextBoltDelay) {
      this.bolts.push(this._createBolt());
      this.flashOpacity = 1;
      this.lastBoltTime = now;
      this.nextBoltDelay = this._randomDelay();

      // Spawn ambient crackles alongside the main bolt
      var crackleCount = 2 + Math.floor(Math.random() * 4);
      for (var ac = 0; ac < crackleCount; ac++) {
        this.ambientCrackles.push(this._spawnAmbientCrackle());
      }
    }

    // Draw ambient crackles
    for (var j = this.ambientCrackles.length - 1; j >= 0; j--) {
      var crackle = this.ambientCrackles[j];
      this._drawCrackle(crackle);
      crackle.life -= crackle.decay;
      if (crackle.life <= 0) {
        this.ambientCrackles.splice(j, 1);
      }
    }

    // Draw bolts
    for (var i = this.bolts.length - 1; i >= 0; i--) {
      var bolt = this.bolts[i];
      if (this._isBoltDead(bolt, now)) {
        this.bolts.splice(i, 1);
        continue;
      }
      var alpha = this._getBoltAlpha(bolt, now);
      if (alpha > 0) {
        this._drawBolt(bolt, alpha);
      }
    }
  };

  LightningCanvas.prototype.destroy = function () {
    this.running = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    window.removeEventListener('resize', this._onResize);
    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
    }
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this.canvas.parentElement) {
      this.canvas.parentElement.removeChild(this.canvas);
    }
  };

  // Init
  function init() {
    var sections = document.querySelectorAll('[data-product-carousel-3d]');
    for (var i = 0; i < sections.length; i++) {
      var container = sections[i].querySelector('.pc3d-fullscreen');
      if (container && !container._lightning) {
        container._lightning = new LightningCanvas(container);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var container = e.target.querySelector('.pc3d-fullscreen');
    if (container) {
      if (container._lightning) container._lightning.destroy();
      container._lightning = new LightningCanvas(container);
    }
  });
})();
